import type { BadgeTone } from '@/components/ui';
import { isApiError } from '@/lib/api';
import { klTime } from '@/pages/calendar/time';
import type {
  StaffVideoSession,
  VideoBlocker,
  VideoEndReason,
  VideoOutcomeResult,
  VideoPaymentState,
  VideoStatus,
} from './types';

export const STATUS_LABEL: Record<
  VideoStatus,
  { text: string; tone: BadgeTone }
> = {
  scheduled: { text: 'Not in waiting room', tone: 'neutral' },
  waiting: { text: 'In waiting room', tone: 'warning' },
  admitted: { text: 'Admitted · check identity', tone: 'info' },
  in_call: { text: 'In call', tone: 'success' },
  ended: { text: 'Ended', tone: 'neutral' },
  cancelled: { text: 'Cancelled', tone: 'neutral' },
  expired: { text: 'Expired', tone: 'danger' },
};

/** Pre-join blockers (RL-1002), worded for the doctor. */
export const BLOCKER_LABEL: Record<VideoBlocker, string> = {
  triage_required: 'Triage answers not given yet',
  triage_not_suitable: 'Triage answers say video is not suitable',
  consent_required: 'Video consent not accepted yet',
  payment_required: 'Consult fee due at the counter',
};

export const OUTCOME_LABEL: Record<VideoOutcomeResult, string> = {
  complete: 'Consult complete',
  come_in: 'Please come in for an examination',
  incomplete: 'Consult incomplete',
  dropped: 'Call dropped',
};

export const OUTCOME_HINT: Record<VideoOutcomeResult, string> = {
  complete: 'Notes, prescription and follow-up are in the record. Fee stands.',
  come_in:
    'Book an in-person visit. Fee stands and is credited to the visit at the counter.',
  incomplete: 'Could not be finished. Nothing charged by default.',
  dropped:
    'The call dropped and the patient did not rejoin. Nothing charged by default.',
};

/** Fee stands by default (RL-1004 DEFAULT_CHARGEABLE). */
export const DEFAULT_CHARGEABLE: Record<VideoOutcomeResult, boolean> = {
  complete: true,
  come_in: true,
  incomplete: false,
  dropped: false,
};

export const END_REASON_LABEL: Record<VideoEndReason, string> = {
  doctor_ended: 'Ended by the doctor',
  teleconsult_closed: 'Teleconsult closed',
  converted: 'Moved to in-person',
  cancelled: 'Cancelled before admitting',
  expired: 'Expired',
  patient_dropped: 'Patient dropped and did not rejoin',
};

export const PAYMENT_LABEL: Record<VideoPaymentState, string> = {
  not_required: 'No fee',
  recorded: 'Fee recorded at booking',
  due: 'Fee due at counter',
  pay_later: 'Pay after the call',
  paid: 'Paid',
  covered: 'Panel covers',
  waived: 'Fee waived',
};

export const readyText = (v: boolean | null) =>
  v === null ? 'not checked' : v ? 'ready' : 'not ready';

export const money = (sen: number) => `RM ${(sen / 100).toFixed(2)}`;

export const isLive = (s: Pick<StaffVideoSession, 'status'>) =>
  ['scheduled', 'waiting', 'admitted', 'in_call'].includes(s.status);

/**
 * Why the doctor cannot run this session's call at all, or null. The
 * doctor's token works from 15 minutes before the slot (window opensAt)
 * until the session is over; only the doctor on the slot may run it.
 */
export function accessDisabledReason(
  s: StaffVideoSession,
  canManage: boolean,
  now: Date = new Date(),
): string | null {
  if (!canManage) return 'You cannot run video calls with this role.';
  if (!s.mine)
    return `Only ${s.doctor.fullName} (the doctor on this slot) can run it.`;
  if (!isLive(s)) return 'This session is over.';
  if (now < new Date(s.window.opensAt))
    return `Opens at ${klTime(s.window.opensAt)} (15 minutes before).`;
  return null;
}

/** The pre-join steps still outstanding (RL-1002 blockers), or null. */
export function admitBlockedReason(s: StaffVideoSession): string | null {
  if (s.blockers.length === 0) return null;
  return `The patient cannot join yet: ${s.blockers
    .map((b) => BLOCKER_LABEL[b])
    .join('; ')}.`;
}

/**
 * Why "Join" is disabled in the list, or null: no access, or a pre-join
 * step is outstanding (the patient could not be admitted, 409
 * VIDEO_JOIN_BLOCKED). Sessions already admitted are never blocked.
 */
export function joinDisabledReason(
  s: StaffVideoSession,
  canManage: boolean,
  now: Date = new Date(),
): string | null {
  return (
    accessDisabledReason(s, canManage, now) ??
    (s.status === 'admitted' || s.status === 'in_call'
      ? null
      : admitBlockedReason(s))
  );
}

/** Error text for an API failure: the API's message plus what to do for known codes. */
export function videoErrorText(err: unknown): {
  title: string;
  body?: string;
  requestId?: string;
} {
  if (!isApiError(err)) {
    const msg = err instanceof Error ? err.message : undefined;
    return { title: 'Something went wrong', body: msg };
  }
  const p: Record<string, unknown> = err.problem ?? {};
  const base = { requestId: err.requestId };
  switch (err.code) {
    case 'VIDEO_WAITING_ROOM_NOT_OPEN':
      return {
        ...base,
        title: 'The call is not open yet',
        body:
          typeof p.opensAt === 'string'
            ? `It opens at ${klTime(p.opensAt)}.`
            : err.message,
      };
    case 'VIDEO_JOIN_BLOCKED': {
      const blockers = Array.isArray(p.blockers)
        ? (p.blockers as VideoBlocker[])
        : [];
      return {
        ...base,
        title: 'The patient cannot join yet',
        body: blockers.length
          ? blockers.map((b) => BLOCKER_LABEL[b] ?? b).join('; ')
          : err.message,
      };
    }
    case 'VIDEO_SESSION_VERSION_CONFLICT':
      return {
        ...base,
        title: 'The session changed meanwhile',
        body: 'It has been reloaded. Check it and try again.',
      };
    case 'RECORDING_DISABLED':
      return {
        ...base,
        title: 'Recording is switched off on this server',
        body: err.message,
      };
    case 'RECORDING_STORAGE_UNAVAILABLE':
      return {
        ...base,
        title: 'Recordings cannot be saved on this server',
        body: 'The private storage is not set up. Nothing was recorded.',
      };
    case 'RECORDING_START_FAILED':
    case 'RECORDING_STOP_FAILED':
      return {
        ...base,
        title: 'The video service did not answer',
        body: 'Try again in a moment. If this keeps happening, tell your administrator.',
      };
    case 'RECORDING_NOT_READY':
      return {
        ...base,
        title: 'The recording is not ready yet',
        body: 'It is still being prepared. It appears here a few minutes after the call.',
      };
    case 'RECORDING_NOT_YOURS':
      return {
        ...base,
        title: 'Only the doctor of this consult can use its recordings',
        body: err.message,
      };
    case 'VIDEO_UNAVAILABLE':
      return {
        ...base,
        title: 'Video calls are switched off on this server',
        body: err.message,
      };
    case 'VIDEO_PROVIDER_FAILED':
      return {
        ...base,
        title: 'The video service did not answer',
        body: 'Try again in a moment.',
      };
    case 'RATE_LIMITED':
      return {
        ...base,
        title: 'Too many call tokens',
        body: 'Wait a moment before trying again.',
      };
    case 'VIDEO_SESSION_NOT_YOURS':
      return {
        ...base,
        title: 'Only the doctor on this slot can run its call',
      };
    case 'NETWORK_ERROR':
    case 'TIMEOUT':
      return {
        ...base,
        title: 'No connection to the server',
        body: 'Check the network and try again.',
      };
    default:
      return {
        ...base,
        title: err.problem?.title ?? 'Not saved',
        body: err.message,
      };
  }
}
